import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  List,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { brand } from "../../theme/themePrimitives";
import { MainMenuItem } from "./sidebarData";

export interface MenuItemProps {
  item: MainMenuItem;
  index?: number;
  openMenu?: number | null;
  onMenuToggle?: (id: number) => void;
  sidebarOpen: boolean;
  sidebarShade?: string;
  sidebarTextColor?: string;
  onNavigate?: (e: React.MouseEvent, route: string) => void;
  mobileView?: boolean;
  setSidebarOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  nested?: boolean;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  item,
  index,
  openMenu,
  onMenuToggle,
  sidebarOpen,
  sidebarShade,
  sidebarTextColor,
  onNavigate,
  mobileView,
  setSidebarOpen,
  nested = false,
}) => {
  const textColor = sidebarTextColor ?? brand[200];
  const location = useLocation();
  const hasChildren = item.subMenus && item.subMenus.length > 0;
  const isExpanded = openMenu === index;
  const displayTooltip = item.tooltip || item.label;

  // Determine active state
  const isActive =
    (item.route && location.pathname.startsWith(item.route)) ||
    (hasChildren &&
      item.subMenus?.some(
        (sub) => sub.route && location.pathname.startsWith(sub.route),
      ));

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren && onMenuToggle && index !== undefined) {
      e.preventDefault();
      onMenuToggle(index);
      return;
    }

    if (item.onClick) {
      item.onClick(e);
    } else if (item.route && onNavigate) {
      onNavigate(e, item.route);
    }

    if (mobileView && setSidebarOpen && !item.keepSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const handleChildNavigation = (e: React.MouseEvent, route: string) => {
    if (onNavigate) {
      onNavigate(e, route);
    }
    if (mobileView && setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  const buttonStyles: SxProps<Theme> = {
    minHeight: 40,
    px: sidebarOpen ? (nested ? 3 : 2) : 0,
    py: 1,
    backgroundColor:
      isActive && sidebarShade ? `${sidebarShade} !important` : "",
    "&:hover": {
      backgroundColor: sidebarShade ? `${sidebarShade} !important` : "",
    },
    justifyContent: sidebarOpen ? "flex-start" : "center",
    alignItems: "center",
    width: sidebarOpen ? "auto" : "100%",
    display: "flex",
    borderRadius: sidebarOpen ? "8px" : "0",
    ...(nested && sidebarOpen && { paddingLeft: "48px !important" }),
  };

  const isSvgComponent =
    typeof item.icon === "function" ||
    (typeof item.icon === "object" &&
      item.icon !== null &&
      "$$typeof" in item.icon &&
      !React.isValidElement(item.icon));

  const iconElement = item.icon ? (
    typeof item.icon === "string" ? (
      <img
        src={item.icon}
        alt={displayTooltip}
        style={{
          height: "24px",
          width: "24px",
          display: "block",
          objectFit: "contain",
          flexShrink: 0,
        }}
      />
    ) : isSvgComponent ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          flexShrink: 0,
          color: textColor,
          "& > svg": {
            width: "24px",
            height: "24px",
          },
        }}
      >
        {React.createElement(
          item.icon as React.ComponentType<React.SVGAttributes<SVGSVGElement>>,
          {
            style: { width: 24, height: 24 },
          },
        )}
      </Box>
    ) : (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "24px",
          height: "24px",
          flexShrink: 0,
          "& > *": {
            width: "24px",
            height: "24px",
          },
        }}
      >
        {item.icon as React.ReactNode}
      </Box>
    )
  ) : null;

  const iconContent =
    iconElement &&
    (sidebarOpen ? (
      iconElement
    ) : (
      <Tooltip title={displayTooltip} placement="top" arrow>
        <Box component="span" sx={{ display: "inline-flex" }}>
          {iconElement}
        </Box>
      </Tooltip>
    ));

  const expandIcon = hasChildren && sidebarOpen && (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: "auto",
        flexShrink: 0,
        width: 24,
        height: 24,
      }}
    >
      {isExpanded ? (
        <ExpandLess
          sx={{
            color: `${textColor} !important`,
            width: "20px",
            height: "20px",
          }}
        />
      ) : (
        <ExpandMore
          sx={{
            color: `${textColor} !important`,
            width: "20px",
            height: "20px",
          }}
        />
      )}
    </Box>
  );

  const buttonContent = (
    <>
      {iconContent && (
        <ListItemIcon
          sx={{
            minWidth: sidebarOpen ? 40 : "auto",
            width: sidebarOpen ? 40 : "100%",
            justifyContent: "center",
            alignItems: "center",
            color: `${textColor} !important`,
            margin: "0 !important",
            padding: 0,
            display: "flex",
            flexShrink: 0,
          }}
        >
          {iconContent}
        </ListItemIcon>
      )}
      {sidebarOpen && (
        <ListItemText
          primary={item.label}
          sx={{
            color: textColor,
            margin: 0,
            flex: 1,
            minWidth: 0,
            "& .MuiListItemText-primary": {
              fontWeight: "600 !important",
              fontSize: "0.875rem",
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "&:hover": {
              color: textColor,
              textDecoration: `underline`,
            },
          }}
        />
      )}
      {expandIcon}
    </>
  );

  // Render with children (parent item)
  if (hasChildren) {
    return (
      <>
        <ListItem
          disablePadding
          sx={{ display: "block", mb: sidebarOpen ? "4px" : "2px" }}
        >
          <ListItemButton
            onClick={handleClick}
            sx={{
              ...buttonStyles,
              ...(isExpanded &&
                sidebarShade && {
                  backgroundColor: `${sidebarShade} !important`,
                }),
            }}
          >
            {buttonContent}
          </ListItemButton>
        </ListItem>
        <Collapse
          in={isExpanded}
          timeout="auto"
          unmountOnExit
          sx={{ minHeight: "auto !important" }}
        >
          <List sx={{ padding: 0 }}>
            {item.subMenus!.map((subItem) => {
              const childMenuItem: MainMenuItem = {
                id: subItem.id,
                label: subItem.label,
                icon: subItem.icon,
                route: subItem.route,
                tooltip: subItem.tooltip,
              };

              return (
                <MenuItem
                  key={subItem.route}
                  item={childMenuItem}
                  sidebarOpen={sidebarOpen}
                  sidebarShade={sidebarShade}
                  sidebarTextColor={sidebarTextColor}
                  onNavigate={handleChildNavigation}
                  mobileView={mobileView}
                  setSidebarOpen={setSidebarOpen}
                  nested={true}
                />
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  // Render with route (navigation) - only if no onClick handler
  if (item.route && !item.onClick) {
    return (
      <ListItem
        disablePadding
        sx={{ display: "block", mb: sidebarOpen ? "4px" : "2px" }}
      >
        <Link
          to={item.route}
          onClick={(e) => {
            if (onNavigate) onNavigate(e, item.route!);
            if (mobileView && setSidebarOpen) setSidebarOpen(false);
          }}
          style={{ textDecoration: "none", color: "inherit", fontWeight: 600 }}
        >
          <ListItemButton sx={buttonStyles}>{buttonContent}</ListItemButton>
        </Link>
      </ListItem>
    );
  }

  // Render button with onClick or no route
  return (
    <ListItem
      disablePadding
      sx={{ display: "block", mb: sidebarOpen ? "4px" : "2px" }}
    >
      <ListItemButton onClick={handleClick} sx={buttonStyles}>
        {buttonContent}
      </ListItemButton>
    </ListItem>
  );
};
